import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button, Card, Row, Col, Form, Container, Spinner, Alert } from 'react-bootstrap';
import { FaPrint, FaDownload, FaCog, FaArrowLeft } from 'react-icons/fa';
import axios from 'axios';
import ReactToPrint from 'react-to-print';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './PatientTicket.css';

const PatientTicket = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const ticketRef = useRef();
  
  // Detect ticket type from URL path
  const getTicketTypeFromPath = () => {
    if (location.pathname.includes('/ticket/appointment/')) return 'appointment';
    if (location.pathname.includes('/ticket/receipt/')) return 'receipt';
    if (location.pathname.includes('/ticket/queue/')) return 'queue';
    return 'appointment';
  };
  
  const [ticketData, setTicketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ticketType, setTicketType] = useState(getTicketTypeFromPath());
  const [ticketSize, setTicketSize] = useState(() => {
    const savedPrinterType = localStorage.getItem('printerType');
    if (savedPrinterType === 'portable') return 'portable';
    return localStorage.getItem('ticketSize') || 'medium';
  });
  const [orientation, setOrientation] = useState(() => localStorage.getItem('ticketOrientation') || 'portrait');
  const [showSettings, setShowSettings] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTicketSize, setNewTicketSize] = useState({ name: '', width: 100, height: 150 });
  const [printerType, setPrinterType] = useState(() => localStorage.getItem('printerType') || 'regular');
  const [clinic, setClinic] = useState(null);
  
  // Predefined sizes in mm (width x height)
  const getTicketSizes = () => {
    const defaultSizes = {
      small: { width: 80, height: 150 },
      medium: { width: 100, height: 180 },
      large: { width: 150, height: 200 },
      portable: { width: 58, height: 'auto' }
    };
    if (clinic?.ticketSizes) {
      return { ...defaultSizes, ...clinic.ticketSizes };
    }
    return defaultSizes;
  };
  
  const [customSize, setCustomSize] = useState(() => {
    const saved = localStorage.getItem('customTicketSize');
    if (saved) {
      return JSON.parse(saved);
    }
    return {
      width: 100,
      height: 150
    };
  });
  
  // Fetch clinic data
  useEffect(() => {
    const fetchClinicData = async () => {
      try {
        const res = await axios.get('/api/clinic.php');
        setClinic(res.data);
      } catch (err) {
        console.error('Error fetching clinic data:', err);
        // Set default clinic data if API fails
        setClinic({
          name: 'MedX Arena Hospital',
          address: 'Healthcare Center, Medical District',
          contactNumber: '+92-XXX-XXXXXXX',
          email: 'info@medxarena.com',
          ticketSizes: {}
        });
      }
    };
    
    fetchClinicData();
  }, []);
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        let data = {};
        
        // Determine what type of ticket based on URL param or user selection
        if (ticketType === 'appointment') {
          const response = await axios.get(`/api/appointments/${id}`);
          
          console.log('Appointment ticket data:', response.data);
          console.log('Appointment time from API:', response.data.appointmentTime);
          
          // Security check: Verify user has access to this appointment
          const currentUser = JSON.parse(localStorage.getItem('user'));
          if (currentUser?.role === 'patient' && response.data.patientId !== currentUser.id) {
            setError('Access denied. You can only view your own appointments.');
            setLoading(false);
            return;
          }
          
          data = {
            ...response.data,
            title: 'Appointment Ticket',
            ticketNumber: `APT-${response.data.id}`,
            date: new Date(response.data.appointmentDate).toLocaleDateString(),
            time: response.data.appointmentTime
          };
        } else if (ticketType === 'receipt') {
          const response = await axios.get(`/api/billings.php?id=${id}`);
          
          // Security check: Verify user has access to this bill
          const currentUser = JSON.parse(localStorage.getItem('user'));
          if (currentUser?.role === 'patient' && response.data.patientId !== currentUser.id) {
            setError('Access denied. You can only view your own bills.');
            setLoading(false);
            return;
          }
          
          // Get the date from billDate, date, or createdAt
          const billDateStr = response.data.billDate || response.data.date || response.data.createdAt;
          let formattedDate = 'N/A';
          
          if (billDateStr) {
            try {
              const dateObj = new Date(billDateStr);
              if (!isNaN(dateObj.getTime())) {
                formattedDate = dateObj.toLocaleDateString('en-GB');
              }
            } catch (e) {
              console.error('Error parsing date:', e);
            }
          }
          
          data = {
            ...response.data,
            title: 'Payment Receipt',
            ticketNumber: response.data.billNumber || `BILL-${response.data.id}`,
            date: formattedDate,
            amount: response.data.totalAmount
          };
        } else if (ticketType === 'queue') {
          const response = await axios.get(`/api/queue/${id}`);
          
          // Security check: Verify user has access to this queue ticket
          const currentUser = JSON.parse(localStorage.getItem('user'));
          if (currentUser?.role === 'patient' && response.data.patientId !== currentUser.id) {
            setError('Access denied. You can only view your own queue tickets.');
            setLoading(false);
            return;
          }
          
          console.log('Queue ticket data:', response.data);
          
          // Handle both single object and array response
          let queueData = response.data;
          if (Array.isArray(response.data) && response.data.length > 0) {
            queueData = response.data[0];
          }
          
          data = {
            ...queueData,
            title: 'Queue Ticket',
            ticketNumber: `Q-${queueData.queueNumber}`,
            date: new Date(queueData.createdAt || queueData.registeredAt).toLocaleDateString(),
            position: queueData.queueNumber
          };
        }
        
        setTicketData(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        if (err.response?.status === 403) {
          setError('Access denied. You do not have permission to view this ticket.');
        } else {
          setError('Failed to load ticket data');
        }
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, ticketType]);
  
  const handleDownloadPDF = async () => {
    if (!ticketRef.current) return;
    
    const canvas = await html2canvas(ticketRef.current, {
      scale: 2,
      backgroundColor: '#ffffff'
    });
    
    const imgData = canvas.toDataURL('image/png');
    
    // Calculate PDF size from ticket size
    const currentSize = getCurrentSize();
    const pdfWidth = currentSize.width * 0.352778; // mm to pt
    const pdfHeight = (currentSize.height === 'auto' ? 150 : currentSize.height) * 0.352778; // mm to pt
    
    const pdf = new jsPDF(orientation, 'pt', [pdfWidth, pdfHeight]);
    const imgProps = pdf.getImageProperties(imgData);
    const pdfImgWidth = orientation === 'portrait' ? pdfWidth : pdfHeight;
    const pdfImgHeight = (imgProps.height * pdfImgWidth) / imgProps.width;
    
    pdf.addImage(imgData, 'PNG', 0, 0, pdfImgWidth, pdfImgHeight);
    pdf.save(`patient_ticket_${id}.pdf`);
  };
  
  const handleSizeChange = (e) => {
    const size = e.target.value;
    setTicketSize(size);
    localStorage.setItem('ticketSize', size);
  };
  
  const handleCustomSizeChange = (e) => {
    const { name, value } = e.target;
    const newSize = {
      ...customSize,
      [name]: parseInt(value)
    };
    setCustomSize(newSize);
    localStorage.setItem('customTicketSize', JSON.stringify(newSize));
    // Update ticket sizes dynamically
    const sizes = getTicketSizes();
    sizes.custom = newSize;
  };
  
  const getCurrentSize = () => {
    const sizes = getTicketSizes();
    return sizes[ticketSize] || { width: 100, height: 150 };
  };
  
  // Get CSS style object for the ticket based on current size and orientation
  const getTicketStyle = () => {
    const size = getCurrentSize();
    
    return {
      width: `${size.width}mm`,
      height: `${size.height}mm`,
      maxWidth: '100%',
      margin: '0 auto',
      padding: ticketSize === 'small' ? '5mm' : '10mm'
    };
  };
  
  // Font size classes based on ticket size
  const getFontSizeClass = () => {
    if (printerType === 'portable') return 'ticket-text-portable';
    switch (ticketSize) {
      case 'small': return 'ticket-text-small';
      case 'large': return 'ticket-text-large';
      case 'portable': return 'ticket-text-portable';
      default: return 'ticket-text-medium';
    }
  };
  
  if (loading) {
    return (
      <Container className="my-4">
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading ticket data...</p>
        </div>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container className="my-4">
        <Alert variant="danger">
          {error}
          <div className="mt-2">
            <Button variant="secondary" onClick={() => navigate(-1)}>Go Back</Button>
          </div>
        </Alert>
      </Container>
    );
  }
  
  if (!ticketData) {
    return (
      <Container className="my-4">
        <Alert variant="warning">
          No ticket data available.
          <div className="mt-2">
            <Button variant="secondary" onClick={() => navigate(-1)}>Go Back</Button>
          </div>
        </Alert>
      </Container>
    );
  }
  
  return (
    <Container className="my-4">
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <Button 
                variant="outline-secondary" 
                className="me-2"
                onClick={() => navigate(-1)}
              >
                <FaArrowLeft /> Back
              </Button>
              <h2 className="d-inline-block ms-2">Patient Ticket</h2>
            </div>
            <div>
              <Button 
                variant="outline-secondary" 
                className="me-2"
                onClick={() => setShowSettings(!showSettings)}
              >
                <FaCog /> Settings
              </Button>
              
              <Button 
                variant="primary" 
                className="me-2"
                onClick={() => {
                  if (printerType === 'portable') {
                    const style = document.createElement('style');
                    style.innerHTML = `
                      @page { size: 58mm auto; margin: 0; }
                      @media print {
                        body { margin: 0; padding: 0; }
                      }
                    `;
                    document.head.appendChild(style);
                    
                    // Hide all UI elements
                    document.querySelectorAll('.btn, .card-header, h2, .d-flex.justify-content-between').forEach(el => {
                      el.style.display = 'none';
                    });
                    
                    setTimeout(() => {
                      window.print();
                      
                      // Restore UI after print
                      setTimeout(() => {
                        document.head.removeChild(style);
                        document.querySelectorAll('.btn, .card-header, h2, .d-flex.justify-content-between').forEach(el => {
                          el.style.display = '';
                        });
                      }, 500);
                    }, 50);
                  } else {
                    window.print();
                  }
                }}
              >
                <FaPrint className="me-1" /> Print
              </Button>
              
              <Button variant="success" onClick={handleDownloadPDF}>
                <FaDownload className="me-1" /> Download
              </Button>
            </div>
          </div>
        </Col>
      </Row>
      
      {showSettings && (
        <Card className="mb-4">
          <Card.Header>Ticket Settings</Card.Header>
          <Card.Body>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Printer Type</Form.Label>
                  <Form.Select 
                    value={printerType}
                    onChange={(e) => {
                      const type = e.target.value;
                      setPrinterType(type);
                      localStorage.setItem('printerType', type);
                      if (type === 'portable') {
                        setTicketSize('portable');
                        localStorage.setItem('ticketSize', 'portable');
                      }
                    }}
                  >
                    <option value="regular">Regular Printer</option>
                    <option value="portable">Portable Thermal Printer (58mm)</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Ticket Type</Form.Label>
                  <Form.Select 
                    value={ticketType}
                    onChange={(e) => setTicketType(e.target.value)}
                  >
                    <option value="appointment">Appointment Ticket</option>
                    <option value="receipt">Payment Receipt</option>
                    <option value="queue">Queue Ticket</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Ticket Size</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Select 
                      value={ticketSize}
                      onChange={handleSizeChange}
                      disabled={printerType === 'portable'}
                    >
                      <option value="small">Small (80x150mm)</option>
                      <option value="medium">Medium (100x180mm)</option>
                      <option value="large">Large (150x200mm)</option>
                      <option value="portable">Portable (58mm)</option>
                      {clinic?.ticketSizes && Object.keys(clinic.ticketSizes)
                        .filter(k => !['small', 'medium', 'large', 'portable'].includes(k))
                        .map(key => {
                          const size = clinic.ticketSizes[key];
                          return (
                            <option key={key} value={key}>
                              {key} ({size.width}x{size.height}mm)
                            </option>
                          );
                        })}
                    </Form.Select>
                    <Button 
                      variant="success" 
                      size="sm"
                      onClick={() => setShowCreateModal(true)}
                      disabled={printerType === 'portable'}
                      style={{whiteSpace: 'nowrap'}}
                    >
                      + Create
                    </Button>
                  </div>
                </Form.Group>
              </Col>
              
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Orientation</Form.Label>
                  <Form.Select 
                    value={orientation}
                    onChange={(e) => {
                      setOrientation(e.target.value);
                      localStorage.setItem('ticketOrientation', e.target.value);
                    }}
                    disabled={printerType === 'portable'}
                  >
                    <option value="portrait">Portrait</option>
                    <option value="landscape">Landscape</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            {ticketSize === 'custom' && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Width (mm)</Form.Label>
                    <Form.Control 
                      type="number" 
                      name="width"
                      value={customSize.width}
                      onChange={handleCustomSizeChange}
                      min="50"
                      max="300"
                    />
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Height (mm)</Form.Label>
                    <Form.Control 
                      type="number"
                      name="height"
                      value={customSize.height}
                      onChange={handleCustomSizeChange}
                      min="50"
                      max="300"
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>
      )}

      {showCreateModal && (
        <Card className="mb-4">
          <Card.Header>Create Custom Ticket Size</Card.Header>
          <Card.Body>
            <Form onSubmit={async (e) => {
              e.preventDefault();
              const { name, width, height } = newTicketSize;
              if (!name || !width || !height) {
                alert('Please fill all fields');
                return;
              }
              
              const newSize = { width: parseInt(width), height: parseInt(height) };
              const updatedSizes = { ...clinic.ticketSizes, [name]: newSize };
              
              try {
                const res = await axios.put('/api/clinic.php', { ticketSizes: updatedSizes });
                setClinic(res.data);
                setTicketSize(name);
                localStorage.setItem('ticketSize', name);
                setShowCreateModal(false);
                setNewTicketSize({ name: '', width: 100, height: 150 });
                alert('Ticket size created successfully!');
              } catch (err) {
                console.error('Error creating ticket size:', err);
                alert('Failed to create ticket size');
              }
            }}>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Size Name</Form.Label>
                    <Form.Control 
                      type="text" 
                      placeholder="e.g. MyCustomSize"
                      value={newTicketSize.name}
                      onChange={(e) => setNewTicketSize({...newTicketSize, name: e.target.value})}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Width (mm)</Form.Label>
                    <Form.Control 
                      type="number" 
                      value={newTicketSize.width}
                      onChange={(e) => setNewTicketSize({...newTicketSize, width: e.target.value})}
                      min="50"
                      max="300"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Height (mm)</Form.Label>
                    <Form.Control 
                      type="number" 
                      value={newTicketSize.height}
                      onChange={(e) => setNewTicketSize({...newTicketSize, height: e.target.value})}
                      min="50"
                      max="300"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Label>&nbsp;</Form.Label>
                  <div className="d-flex gap-2">
                    <Button variant="primary" type="submit">Save</Button>
                    <Button variant="secondary" onClick={() => {
                      setShowCreateModal(false);
                      setNewTicketSize({ name: '', width: 100, height: 150 });
                    }}>Cancel</Button>
                  </div>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
      )}
      
      {ticketData && (
        <div className="ticket-container">
          <div
            ref={ticketRef}
            className={`ticket ${getFontSizeClass()} ${orientation} ${printerType === 'portable' ? 'portable-printer' : ''}`}
            style={getTicketStyle()}
          >
            <div className="ticket-header text-center">
              <h3 className="clinic-name">{clinic?.name || 'Medical Clinic'}</h3>
              {clinic?.address && <p className="clinic-address">{clinic.address}</p>}
              {clinic?.contactNumber && <p className="clinic-contact">{clinic.contactNumber}</p>}
              <p className="ticket-title">{ticketData.title}</p>
              <div className="ticket-divider"></div>
            </div>
            
            <div className="ticket-body">
              <div className="ticket-row">
                <span className="ticket-label">Ticket No:</span>
                <span className="ticket-value">{ticketData.ticketNumber}</span>
              </div>
              
              {ticketType === 'appointment' && (
                <>
                  <div className="ticket-row">
                    <span className="ticket-label">Booked On:</span>
                    <span className="ticket-value">
                      {(() => {
                        // Parse the datetime string and add Pakistan timezone offset (UTC+5)
                        const dateStr = ticketData.createdAt.replace(' ', 'T');
                        const date = new Date(dateStr);
                        
                        // Add 5 hours for Pakistan timezone
                        date.setHours(date.getHours() + 5);
                        
                        const formattedDate = date.toLocaleDateString('en-GB');
                        const hours = date.getHours();
                        const minutes = date.getMinutes().toString().padStart(2, '0');
                        const ampm = hours >= 12 ? 'PM' : 'AM';
                        const formattedHour = hours % 12 || 12;
                        return `${formattedDate} ${formattedHour}:${minutes} ${ampm}`;
                      })()
                    }
                    </span>
                  </div>
                  
                  <div className="ticket-row">
                    <span className="ticket-label">Appointment:</span>
                    <span className="ticket-value">
                      {new Date(ticketData.appointmentDate).toLocaleDateString('en-GB')} {ticketData.appointmentTime ? (() => {
                        console.log('Appointment time debug:', ticketData.appointmentTime);
                        const timeStr = ticketData.appointmentTime;
                        if (timeStr && timeStr.match(/^\d{1,2}:\d{2}$/)) {
                          const [hours, minutes] = timeStr.split(':');
                          const hour = parseInt(hours, 10);
                          const ampm = hour >= 12 ? 'PM' : 'AM';
                          const formattedHour = hour % 12 || 12;
                          return `${formattedHour}:${minutes} ${ampm}`;
                        }
                        return timeStr;
                      })() : 'N/A'}
                    </span>
                  </div>
                  
                  <div className="ticket-row">
                    <span className="ticket-label">Patient:</span>
                    <span className="ticket-value">{ticketData.Patient?.name}</span>
                  </div>
                  
                  <div className="ticket-row">
                    <span className="ticket-label">Doctor:</span>
                    <span className="ticket-value">{ticketData.Doctor?.name}</span>
                  </div>
                  
                  <div className="ticket-row">
                    <span className="ticket-label">Status:</span>
                    <span className="ticket-value">{ticketData.status}</span>
                  </div>
                  
                  {ticketData.notes && ticketData.notes.includes('Rescheduled:') && (
                    <>
                      <div className="ticket-divider" style={{margin: '10px 0'}}></div>
                      <div style={{textAlign: 'center', padding: '8px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '5px', marginBottom: '10px'}}>
                        <strong style={{color: '#856404'}}>⚠️ RESCHEDULED APPOINTMENT</strong>
                      </div>
                      
                      {ticketData.notes.includes('Original:') && (
                        <div className="ticket-row">
                          <span className="ticket-label">Original Date:</span>
                          <span className="ticket-value" style={{fontSize: '0.85em', color: '#666'}}>
                            {(() => {
                              const originalText = ticketData.notes.split('Original: ')[1]?.split('\n')[0];
                              if (originalText) {
                                const parts = originalText.split(' at ');
                                if (parts.length === 2) {
                                  const [year, month, day] = parts[0].split('-');
                                  return `${day}/${month}/${year} at ${parts[1]}`;
                                }
                              }
                              return 'N/A';
                            })()
                            }
                          </span>
                        </div>
                      )}
                      
                      <div className="ticket-row">
                        <span className="ticket-label">New Date:</span>
                        <span className="ticket-value" style={{fontWeight: 'bold', color: '#28a745'}}>
                          {new Date(ticketData.appointmentDate).toLocaleDateString('en-GB')} {ticketData.appointmentTime ? (() => {
                            const [hours, minutes] = ticketData.appointmentTime.split(':');
                            const hour = parseInt(hours);
                            const ampm = hour >= 12 ? 'PM' : 'AM';
                            const formattedHour = hour % 12 || 12;
                            return `${formattedHour}:${minutes} ${ampm}`;
                          })() : 'N/A'}
                        </span>
                      </div>
                      
                      <div className="ticket-row">
                        <span className="ticket-label">Reason:</span>
                        <span className="ticket-value" style={{fontSize: '0.85em'}}>
                          {(() => {
                            const fullNotes = ticketData.notes;
                            if (fullNotes && fullNotes.includes('Rescheduled: ')) {
                              // Get text after "Rescheduled: "
                              const reasonPart = fullNotes.split('Rescheduled: ')[1];
                              if (reasonPart) {
                                // Remove any trailing newlines and extra text
                                const cleanReason = reasonPart.split('\n')[0].trim();
                                return cleanReason || 'Appointment rescheduled';
                              }
                            }
                            return 'Appointment rescheduled';
                          })()
                          }
                        </span>
                      </div>
                    </>
                  )}
                </>
              )}
              
              {ticketType !== 'appointment' && (
                <div className="ticket-row">
                  <span className="ticket-label">Date:</span>
                  <span className="ticket-value">{ticketData.date}</span>
                </div>
              )}
              
              {ticketType === 'receipt' && (
                <>
                  <div className="ticket-row">
                    <span className="ticket-label">Patient:</span>
                    <span className="ticket-value">{ticketData.Patient?.name}</span>
                  </div>
                  
                  <div className="ticket-row">
                    <span className="ticket-label">Amount:</span>
                    <span className="ticket-value">
                      ${Number(ticketData.totalAmount).toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="ticket-row">
                    <span className="ticket-label">Paid:</span>
                    <span className="ticket-value">
                      ${Number(ticketData.paidAmount).toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="ticket-row">
                    <span className="ticket-label">Balance:</span>
                    <span className="ticket-value">
                      ${(Number(ticketData.totalAmount) - Number(ticketData.paidAmount)).toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="ticket-row">
                    <span className="ticket-label">Status:</span>
                    <span className="ticket-value">{ticketData.paymentStatus}</span>
                  </div>
                </>
              )}
              
              {ticketType === 'queue' && (
                <>
                  <div style={{textAlign: 'center', margin: '20px 0', padding: '15px', border: '3px solid #000', borderRadius: '10px'}}>
                    <div style={{fontSize: '1.2em', fontWeight: 'bold', marginBottom: '5px'}}>TOKEN NUMBER</div>
                    <div style={{fontSize: '3em', fontWeight: 'bold', color: '#000'}}>
                      #{ticketData.queueNumber}
                    </div>
                  </div>
                  
                  <div className="ticket-row">
                    <span className="ticket-label">Patient:</span>
                    <span className="ticket-value" style={{fontWeight: 'bold'}}>{ticketData.Patient?.name || ticketData.patientName}</span>
                  </div>
                  
                  <div className="ticket-row">
                    <span className="ticket-label">Doctor:</span>
                    <span className="ticket-value">Dr. {ticketData.Doctor?.name || ticketData.doctorName}</span>
                  </div>
                  
                  <div className="ticket-row">
                    <span className="ticket-label">Time:</span>
                    <span className="ticket-value">{(() => {
                      const dateStr = (ticketData.registeredAt || ticketData.createdAt).replace(' ', 'T');
                      const date = new Date(dateStr);
                      date.setHours(date.getHours() + 5);
                      
                      const hours = date.getHours();
                      const minutes = date.getMinutes().toString().padStart(2, '0');
                      const ampm = hours >= 12 ? 'PM' : 'AM';
                      const formattedHour = hours % 12 || 12;
                      return `${formattedHour}:${minutes} ${ampm}`;
                    })()}</span>
                  </div>
                  
                  {(ticketData.appointmentTime || ticketData.Appointment?.appointmentTime) && (
                    <div className="ticket-row">
                      <span className="ticket-label">Appointment:</span>
                      <span className="ticket-value">{(() => {
                        const timeStr = ticketData.appointmentTime || ticketData.Appointment?.appointmentTime;
                        console.log('PatientTicket - Appointment time debug:', timeStr);
                        
                        if (timeStr && timeStr.match(/^\d{1,2}:\d{2}$/)) {
                          const [hours, minutes] = timeStr.split(':');
                          const hour = parseInt(hours, 10);
                          const ampm = hour >= 12 ? 'PM' : 'AM';
                          const formattedHour = hour % 12 || 12;
                          return `${formattedHour}:${minutes} ${ampm}`;
                        }
                        return timeStr || 'N/A';
                      })()}</span>
                    </div>
                  )}
                  
                  <div style={{textAlign: 'center', marginTop: '15px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '5px'}}>
                    <div style={{fontSize: '0.9em', fontWeight: 'bold'}}>Please wait for your turn</div>
                    <div style={{fontSize: '0.8em', color: '#666', marginTop: '5px'}}>Your token will be called soon</div>
                  </div>
                </>
              )}
            </div>
            
            <div className="ticket-footer text-center">
              <div className="ticket-divider"></div>
              <p className="thank-you">Thank You For Your Visit!</p>
              <p className="developer-info" style={{fontSize: '0.7em', marginTop: '10px', color: '#666'}}>Developer: Abdullah Arif | +92-3137323866</p>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default PatientTicket; 

